import React, { useState } from 'react';
import Modal from '../../../../../../common/Modal';
import BenchDataFields from '../../../../../../common/BenchDataFields';
import { benchApi } from '../../../../../../../services/api/benchApi';
import { useAlert } from '../../../../../../../contexts/AlertContext';
import { BENCH_TYPES } from '../../../../../../../constants/benchTypes';
import { useAbility } from '../../../../../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../../../../../constants/permissions';

/**
 * Modal for editing bench-specific data (type, star, comments, salary)
 * on an employee's bench assignment.
 *
 * Salary fields are only visible when the user has BenchSalary read permission.
 * Salary editing requires BenchSalary update permission.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.employee - The employee object with benchData
 * @param {Function} props.onSuccess - Callback when update is successful
 */
const EditBenchDataModal = ({
  isOpen,
  onClose,
  employee,
  onSuccess,
  // Optional one-click salary fill (see BenchDataFields.salarySuggestion).
  salarySuggestion = null
}) => {
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize state directly from employee prop — the parent passes a key tied to
  // the employee so this component remounts fresh for each employee, avoiding
  // stale-state and TipTap EditorProvider content-sync issues.
  const bd = employee?.benchData || {};
  const [benchType, setBenchType] = useState(bd.benchType || BENCH_TYPES.BENCH);
  const [flagAsStar, setFlagAsStar] = useState(bd.flagAsStar || false);
  const [comments, setComments] = useState(bd.comments || '');
  const [benchManager, setBenchManager] = useState(bd.benchManager || null);
  const [salary, setSalary] = useState(bd.salary || '');
  const [salaryCurrency, setSalaryCurrency] = useState(bd.salaryCurrency || '');
  const [salaryPeriod, setSalaryPeriod] = useState(bd.salaryPeriod || '');
  const [salaryAmountType, setSalaryAmountType] = useState(bd.salaryAmountType || '');

  // ADR-117 §3 — the amount as it was when this modal opened. The component is keyed
  // on the employee and remounts per row, so this is the baseline for "did the user
  // actually change the amount?". Sending an untouched amount is what used to destroy
  // the stored ciphertext on a currency-only edit.
  const [initialSalary] = useState(bd.salary || '');
  const salaryUnreadable = bd.salaryStatus === 'UNREADABLE';

  const canUpdateSalary = ability.can(ACTIONS.UPDATE, SUBJECTS.BENCH_SALARY);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const updateData = {
        benchType,
        flagAsStar,
        comments: comments || null,
        benchManagerId: benchManager?.id || null,
      };

      // Only include salary fields if the user has permission to update them
      if (canUpdateSalary) {
        // ADR-117 §3 — send the amount ONLY when it actually changed. An untouched
        // field must leave the stored ciphertext byte-identical: for an UNREADABLE
        // salary the field renders empty, and submitting that empty value as a clear
        // would destroy a value this environment simply cannot display.
        if (salary !== initialSalary) {
          updateData.salary = salary || null;
        }
        updateData.salaryCurrency = salaryCurrency || null;
        updateData.salaryPeriod = salaryPeriod || null;
        updateData.salaryAmountType = salaryAmountType || null;
      }

      await benchApi.updateBenchData(employee.employeeProjectId, updateData);

      showAlert('Bench data updated successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating bench data:', error);
      showAlert(error.message || 'Error updating bench data', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!employee) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit Bench Data | ${employee.fullName}`}
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Saving...' : 'Save Changes'}
      submitDisabled={isSubmitting}
      submitButtonProps={{
        sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
      }}
    >
      <BenchDataFields
        benchType={benchType}
        setBenchType={setBenchType}
        flagAsStar={flagAsStar}
        setFlagAsStar={setFlagAsStar}
        comments={comments}
        setComments={setComments}
        benchManager={benchManager}
        setBenchManager={setBenchManager}
        directManager={employee.directManager}
        salary={salary}
        setSalary={setSalary}
        salaryCurrency={salaryCurrency}
        setSalaryCurrency={setSalaryCurrency}
        salaryPeriod={salaryPeriod}
        setSalaryPeriod={setSalaryPeriod}
        salaryAmountType={salaryAmountType}
        setSalaryAmountType={setSalaryAmountType}
        salaryUnreadable={salaryUnreadable}
        disabled={isSubmitting}
        instanceId="edit-bench-data-bench-manager"
        salarySuggestion={salarySuggestion}
      />
    </Modal>
  );
};

export default EditBenchDataModal;
